/** @format **/

import { generateScreen, generateSkipCondition } from "./generator";

const addressScreens = (i18n) => ({
  postalCode: {
    type: "postal-code",
    flowConfirmation: {
      propertyName: "postalCode",
      labelLocale: () => i18n.t("flowConfirmation:address"),
      orderNo: 6.1,
      groupId: "postal",
      groupFormat: "street-name house-number, postal-code city",
    },
  },

  city: {
    type: "city",
    flowConfirmation: {
      groupId: "postal",
      orderNo: 6.2,
    },
  },

  streetName: {
    type: "street-name",
    flowConfirmation: {
      groupId: "postal",
      orderNo: 6.3,
    },
  },

  houseNumber: {
    type: "house-number",
    flowConfirmation: {
      groupId: "postal",
      orderNo: 6.4,
    },
  },

  state: {
    type: "question.state",
    params: {
      list: true,
      scrollShadow: true,
      text: () => i18n.t("postalCode:select_province", { context: "us" }),
      dispatchKey: "updateUser",
      propertyName: "province",
      currentAnswer: (store) => store.state.user.state,
      answers: (store) => {
        return store.state.app.country.provinces.map((p) => ({
          id: p.ISOCode.slice(-2),
          label: p.name,
        }));
      },
      showKeyboardOnNextScreen: true,
      conditions: [
        {
          property: "user.province",
          value: false,
        },
        {
          property: "app.postalArea.streets",
          value: false,
        },
        {
          property: "app.country.code",
          value: "US",
        },
      ],
    },
  },

  province: {
    type: "question.province",
    params: {
      list: true,
      scrollShadow: true,
      text: () => i18n.t("postalCode:select_province"),
      dispatchKey: "updateUser",
      propertyName: "province",
      currentAnswer: (store) => store.state.user.province,
      answers: (store) => {
        return store.state.app.country.provinces
          .filter((item) => {
            return store.state.customData.hideSHProvince === true ||
              (store.state.customData.hideSHProvince === undefined &&
                store.state.partner.hideSHProvince === true)
              ? item.ISOCode !== "DE-SH"
              : true;
          })
          .map((p) => ({
            id: p.ISOCode.slice(-2),
            label: p.name,
          }));
      },
      conditions: [
        {
          property: "user.province",
          value: false,
        },
        {
          property: "user.country",
          value: ["DE", "CA"],
        },
      ],
    },
  },

  fiscalRegion: {
    type: "question.fiscal-region",
    params: {
      list: true,
      scrollShadow: true,
      text: () => i18n.t("regions:select"),
      dispatchKey: "updateUser",
      propertyName: "fiscalRegion",
      currentAnswer: (store) => store.state.user.fiscalRegion,
      answers: (store) => {
        const {
          spain: spanishRegions,
        } = require("../../../utils/fiscal-regions.config");
        spanishRegions(i18n, store);
      },
      conditions: [
        {
          property: "app.country.code",
          value: "ES",
        },
        {
          property: "partner.showSpainFiscalRegion",
          value: true,
        },
      ],
    },
  },
});

export const getSkipCondition = (
  propertyName,
  skipScreensIfStoreValuesAreSet = false
) =>
  skipScreensIfStoreValuesAreSet
    ? { params: { conditions: [generateSkipCondition(propertyName)] } }
    : {};

export const addressFlow = (
  i18n,
  { skipScreensIfStoreValuesAreSet = false } = {}
) => {
  const screens = [
    {
      name: "postalCode",
      params: {
        conditions: [
          ...(skipScreensIfStoreValuesAreSet
            ? [generateSkipCondition("user.postalCode")]
            : []),
        ],
      },
    },
    {
      name: "state",
      params: {
        conditions: [
          ...(skipScreensIfStoreValuesAreSet
            ? [generateSkipCondition("user.state")]
            : []),
        ],
      },
    },
    {
      name: "province",
      params: {
        conditions: [
          ...(skipScreensIfStoreValuesAreSet
            ? [generateSkipCondition("user.province")]
            : []),
        ],
      },
    },
    {
      name: "city",
      params: {
        conditions: [
          ...(skipScreensIfStoreValuesAreSet
            ? [generateSkipCondition("user.city")]
            : []),
        ],
      },
    },
    {
      name: "streetName",
      params: {
        conditions: [
          ...(skipScreensIfStoreValuesAreSet
            ? [generateSkipCondition("user.streetName")]
            : []),
        ],
      },
    },
    {
      name: "houseNumber",
      params: {
        conditions: [
          ...(skipScreensIfStoreValuesAreSet
            ? [generateSkipCondition("user.houseNumber")]
            : []),
        ],
      },
    },
  ];

  return generateScreen(addressScreens, { i18n, screens });
};

export const companyAddressFlow = (
  i18n,
  { skipScreensIfStoreValuesAreSet = false } = {}
) => {
  const screens = [
    {
      name: "postalCode",
      params: {
        dispatchKey: "updateJob",
        propertyName: "companyAddress.postalCode",
        showCountryChange: false,
        conditions: [
          ...(skipScreensIfStoreValuesAreSet
            ? [generateSkipCondition("job.companyAddress.postalCode")]
            : []),
        ],
      },
    },
    {
      name: "state",
      params: {
        dispatchKey: "updateJob",
        propertyName: "companyAddress.province",
        conditions: [
          ...(skipScreensIfStoreValuesAreSet
            ? [generateSkipCondition("job.companyAddress.province")]
            : []),
        ],
      },
    },
    {
      name: "city",
      params: {
        dispatchKey: "updateJob",
        propertyName: "companyAddress.city",
        conditions: [
          ...(skipScreensIfStoreValuesAreSet
            ? [generateSkipCondition("job.companyAddress.city")]
            : []),
        ],
      },
    },
    {
      name: "streetName",
      params: {
        dispatchKey: "updateJob",
        propertyName: "companyAddress.streetName",
        conditions: [
          ...(skipScreensIfStoreValuesAreSet
            ? [generateSkipCondition("job.companyAddress.streetName")]
            : []),
        ],
      },
    },
    {
      name: "houseNumber",
      params: {
        dispatchKey: "updateJob",
        propertyName: "companyAddress.houseNumber",
        conditions: [
          ...(skipScreensIfStoreValuesAreSet
            ? [generateSkipCondition("job.companyAddress.houseNumber")]
            : []),
        ],
      },
    },
  ];

  return generateScreen(addressScreens, {
    i18n,
    screens,
    screenTypePostfix: "company",
  });
};

export const getAddressScreens = generateScreen.bind(null, addressScreens);



// WEBPACK FOOTER //
// ./src/partners/config/screens/address.js